/**
 * Active language as an external store.
 *
 * Not React context: the non-component callers (api/client.ts, api/upload.ts,
 * api/errors.ts, usePieceForm.ts) need the same answer, and a context would
 * force them all to become hooks. `useSyncExternalStore` gives components a
 * re-render on switch without duplicating the source of truth.
 */
import { useSyncExternalStore } from "react";

export type Lang = "fa" | "en";

export const LANGS = ["fa", "en"] as const;

const KEY = "zardir:lang";

/** The user's own choice, or null if they have never made one. */
function stored(): Lang | null {
  try {
    const value = localStorage.getItem(KEY);
    return value === "en" || value === "fa" ? value : null;
  } catch {
    // Private mode or blocked site data. A default is better than a crash.
    return null;
  }
}

let chosen: Lang | null = stored();
let current: Lang = chosen ?? "fa";

const listeners = new Set<() => void>();

export function getLang(): Lang {
  return current;
}

export function dirOf(lang: Lang): "rtl" | "ltr" {
  return lang === "fa" ? "rtl" : "ltr";
}

/** `<html lang>` and `<html dir>` — every logical CSS property follows these. */
export function applyDocumentLang(lang: Lang): void {
  const root = document.documentElement;
  root.lang = lang;
  root.dir = dirOf(lang);
}

function commit(next: Lang): void {
  if (next === current) return;
  current = next;
  applyDocumentLang(next);
  for (const listener of listeners) listener();
}

/** An explicit choice. Persisted, and never overridden by the Telegram locale. */
export function setLang(next: Lang): void {
  chosen = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // The app still works; the choice just will not survive a reload.
  }
  commit(next);
}

/**
 * The Telegram account locale from GET /me. Only a hint — it applies on first
 * run and is ignored once the user has picked a language for themselves.
 */
export function suggestLang(languageCode: string | undefined): void {
  if (chosen) return;
  commit(languageCode?.toLowerCase().startsWith("fa") ? "fa" : "en");
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getLang, getLang);
}
